import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Theme provider — controls light / dark mode for the whole app.
 *
 * The provider stores the user's preference in `localStorage` under
 * `tenaedm:theme` (`light` | `dark` | `system`). When set to `system` we
 * follow the OS via `prefers-color-scheme` and react to changes. The
 * resolved theme is applied as a `dark` class on `<html>` (Tailwind's
 * `darkMode: 'class'` strategy — see `tailwind.config.ts`).
 *
 * Initial render
 * ──────────────
 * The matching script in `index.html` reads the same key synchronously
 * before React boots, so the first paint never flashes the wrong theme
 * (FOUC). The provider then takes over and keeps the class in sync.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  /** User-selected preference (what the toggle reflects). */
  preference: ThemePreference;
  /** Actually applied theme (resolves `system` against the OS). */
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
  /** Quick toggle between light and dark (skips `system`). */
  toggle: () => void;
}

const STORAGE_KEY = 'tenaedm:theme';

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  return 'system';
}

function resolvePreference(preference: ThemePreference): ResolvedTheme {
  if (preference === 'light' || preference === 'dark') return preference;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolvePreference(preference));

  // Apply theme on every resolved change.
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  // Re-resolve when preference changes; also subscribe to OS changes when
  // the user is on `system`.
  useEffect(() => {
    setResolved(resolvePreference(preference));
    if (preference !== 'system') return;
    if (typeof window === 'undefined') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolved(mql.matches ? 'dark' : 'light');
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const toggle = useCallback(() => {
    setPreferenceState((prev) => {
      const current = resolvePreference(prev);
      const next: ThemePreference = current === 'dark' ? 'light' : 'dark';
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, next);
      }
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference, toggle }),
    [preference, resolved, setPreference, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>.');
  return ctx;
}
